import { RoundedLogo } from '@assets/newlogo';
import DesktopAuthWrapper from '@components/atoms/DesktopAuthWrapper';
import Checkbox from '@components/atoms/inputs/Checkbox';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIsLoggedIn, usePasswordless, useProfile } from '@hooks/api';
import { hasErrorCode, ErrorCode } from '@models/api/errors';
import { formStart, formSubmit, signUp } from '@services/analytics';
import {
    AnalyticsAuthMethod,
    AnalyticsFormId,
    AnalyticsFormOutcome,
} from 'mh-client';
import { useLoadingModal } from '@hooks/useLoadingModal';
import { useScreenName } from '@hooks/useScreenName';
import { useTitle } from '@hooks/useTitle';
import { ButtonLabel, InputField } from '@moreharvest/component-library';
import { useAppDispatch } from '@store/index';
import { setEmail } from '@store/reducers/profile';
import { RE_EMAIL } from '@utils/constants';
import { FormattedMessage } from '@utils/i18n';
import { passwordlessRedirect } from '@utils/profile';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { InferType, boolean, object, string } from 'yup';

export interface RegisterInputs {
    email: string;
    acceptTC: boolean;
}

const Register = () => {
    useTitle('register_create_your_account', 'registration');
    const isLoggedIn = useIsLoggedIn();
    const { data: userProfile, isLoading: isProfileLoading } = useProfile();
    const { mutateAsync: passwordless, isPending } = usePasswordless();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const screenName = useScreenName();

    const formStarted = useRef(false);
    const handleFormStart = () => {
        if (!formStarted.current && screenName) {
            formStarted.current = true;
            formStart({
                form_id: AnalyticsFormId.getInstance().REGISTRATION,
                screen_name: screenName,
            });
        }
    };

    const schema = object().shape({
        email: string()
            .matches(RE_EMAIL, 'Please enter a valid email')
            .required('Please enter a valid email'),
        acceptTC: boolean().oneOf([true], 'Please accept the T&C').required(),
    });
    const {
        register,
        formState: { errors },
        reset,
        watch,
        handleSubmit,
        control,
        setError,
    } = useForm({
        resolver: yupResolver(schema),
    });

    useLoadingModal(isPending || isProfileLoading);

    useEffect(() => {
        if (isLoggedIn && userProfile) {
            navigate('/');
        }
    }, [userProfile, isLoggedIn, navigate]);

    const onSubmit = async (data: InferType<typeof schema>) => {
        if (screenName) {
            formSubmit({
                form_id: AnalyticsFormId.getInstance().REGISTRATION,
                screen_name: screenName,
                outcome: AnalyticsFormOutcome.Success,
            });
        }
        dispatch(setEmail(data.email));
        await passwordless({
            email: data.email,
            url: 'login/magic-link',
            force: false,
        })
            .then(({ sent, methods }) => {
                // Existing magic-link-only users still get sent=true
                // (the response hides account existence) and remain counted.
                if (sent) {
                    signUp({ method: AnalyticsAuthMethod.Email });
                }
                const methodNames = methods
                    .asJsReadonlyArrayView()
                    .map(m => m.name);
                navigate(passwordlessRedirect(sent, methodNames));
            })
            .catch((error: unknown) => {
                if (hasErrorCode(error, ErrorCode.InvalidEmail)) {
                    setError('email', {
                        message: intl.formatMessage({
                            id: 'account_incorrect_email',
                        }),
                    });
                } else {
                    setError('email', {
                        message: intl.formatMessage({
                            id: 'oops_something_went_wrong',
                        }),
                    });
                }
            });
    };

    return (
        <DesktopAuthWrapper>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col items-center"
            >
                <RoundedLogo size="small" />
                <h4 className="H4 text-center semibold my-12">
                    <FormattedMessage id="register_create_your_account" />
                </h4>
                <div
                    className="flex flex-col items-start mx-auto w-full max-w-[360px] gap-y-4 mb-8"
                    onFocus={handleFormStart}
                >
                    <InputField
                        placeholder={intl.formatMessage({
                            id: 'email_address',
                        })}
                        id="registration_input_email"
                        variant="default"
                        register={register('email')}
                        errorMessage={
                            errors['email']
                                ? intl.formatMessage({
                                      id: 'account_incorrect_email',
                                  })
                                : undefined
                        }
                        showRightIcon={!!watch('email')}
                        closeIconClick={() => {
                            reset({
                                email: '',
                            });
                        }}
                    />
                    <Controller
                        control={control}
                        name="acceptTC"
                        render={() => (
                            <Checkbox
                                id="registration_click_acceptTermsConditions"
                                register={register('acceptTC')}
                                label={
                                    <FormattedMessage
                                        id="register_accept_tncs"
                                        values={{
                                            a: chunks => (
                                                <a
                                                    href="https://www.moreharvest.com/terms"
                                                    className="underline"
                                                    target="_blank"
                                                >
                                                    {chunks}
                                                </a>
                                            ),
                                        }}
                                    />
                                }
                                errorMessage={errors['acceptTC']?.message}
                            />
                        )}
                    />
                </div>

                <ButtonLabel
                    size="medium"
                    variant="solid"
                    type="submit"
                    id="registration_CTA_verifyEmail"
                >
                    <FormattedMessage id="account_verify_email" />
                </ButtonLabel>
            </form>
        </DesktopAuthWrapper>
    );
};

export default Register;
